import shortid from 'shortid';
import { findLastIndex } from 'lodash';
import selectors from '../rootSelectors';
import { MAZE_FETCH_RETRY } from '../common/api/apiRetryGetConstants';
import MazeServiceClient from '../common/clients/MazeServiceClient';
import { COMPREHENSION, MAZE, RESULT_RESPONSE_OUTCOME } from '../constants/measure';
import { setPlaylistItemCompleted } from '../common/playlist/playlistActions';
import { clearForm, fetchStudentForms } from '../common/api/formsActions';
import { dataDogAddWarning } from '../common/logging/datadog';
import { MODEL, PRACTICE, QUESTIONS } from '../constants/assessmentItemType';
import {
    getStudentResults,
    saveMeasureResponse,
    getMeasureInstruction,
} from '../common/api/mazeActions';
import AssessmentFormsServiceClient from '../common/clients/AssessmentFormsServiceClient';
import { ALL_MAZE_INSTRUCTIONS } from './mazeConstants';

const measure = MAZE.MEASURE;
const measureType = COMPREHENSION;

export const SET_MAZE_CONTENT = 'SET_MAZE_CONTENT';
export const SELECT_MAZE_RESPONSE = 'SELECT_MAZE_RESPONSE';
export const SET_IS_READY_SCRIPT_PLAYED = 'SET_IS_READY_SCRIPT_PLAYED';
export const CLEAR_MAZE_RESPONSES = 'CLEAR_MAZE_RESPONSES';
export const CLEAR_MAZE_SCREEN_MODE = 'CLEAR_MAZE_SCREEN_MODE';
export const MADE_FIRST_ATTEMPT = 'MADE_FIRST_ATTEMPT';
export const HIGHLIGHT_MAZE_RESPONSE = 'HIGHLIGHT_MAZE_RESPONSE';
export const HIGHLIGHT_MAZE_RESPONSES = 'HIGHLIGHT_MAZE_RESPONSES';
export const DEHIGHLIGHT_MAZE_RESPONSES = 'DEHIGHLIGHT_MAZE_RESPONSES';
export const SET_MAZE_RESPONSES_STRUCTURE = 'SET_MAZE_RESPONSES_STRUCTURE';
export const SET_MAZE_SCREEN_MODE = 'SET_MAZE_SCREEN_MODE';

export const SET_IS_MAZE_UNTIMED = 'SET_IS_MAZE_UNTIMED';

const formatParagraphs = paragraphs =>
    paragraphs.map(p => ({
        text: p,
        id: shortid.generate(),
    }));

const formatMazeContent = content => ({
    ...content,
    [PRACTICE]: {
        ...content[PRACTICE],
        paragraphs: formatParagraphs(content[PRACTICE].paragraphs),
    },
    [QUESTIONS]: {
        ...content[QUESTIONS],
        paragraphs: formatParagraphs(content[QUESTIONS].paragraphs),
    },
    [MODEL]: {
        line_model: {
            ...content[MODEL].line_model,
            paragraphs: formatParagraphs(content[MODEL].line_model.paragraphs),
        },
        scroll_model: {
            ...content[MODEL].scroll_model,
            paragraphs: formatParagraphs(content[MODEL].scroll_model.paragraphs),
        },
    },
});

export const setMazeContent = content => ({
    type: SET_MAZE_CONTENT,
    content: formatMazeContent(content),
});

export const setMazeScreenMode = screenMode => ({
    type: SET_MAZE_SCREEN_MODE,
    screenMode,
});

export const selectResponse = (questionIndex, responseIndex) => ({
    type: SELECT_MAZE_RESPONSE,
    questionIndex,
    responseIndex,
});

export const clearResponses = () => ({
    type: CLEAR_MAZE_RESPONSES,
});

export const clearScreenMode = () => ({
    type: CLEAR_MAZE_SCREEN_MODE,
});

export const highlightMazeResponse = (questionIndex, responseIndex) => ({
    type: HIGHLIGHT_MAZE_RESPONSE,
    questionIndex,
    responseIndex,
});

export const highlightMazeResponses = responses => ({
    type: HIGHLIGHT_MAZE_RESPONSES,
    responses,
});

export const dehighlightMazeResponses = () => ({
    type: DEHIGHLIGHT_MAZE_RESPONSES,
});

export const setIsReadyScriptPlayed = isPlayed => ({
    type: SET_IS_READY_SCRIPT_PLAYED,
    isPlayed,
});

export const madeFirstAttempt = () => ({
    type: MADE_FIRST_ATTEMPT,
});

export const createResponsesStructure = responsesItems => ({
    type: SET_MAZE_RESPONSES_STRUCTURE,
    responsesItems,
});

const fetchMazeContentWithReload = hasReloaded => (dispatch, getStore) => {
    const state = getStore();
    const form = selectors.findNextForm(state, measure, measureType);
    if (!form) {
        if (hasReloaded) {
            dataDogAddWarning('fetchMazeContent: form not found after student forms reload');
            return Promise.resolve();
        }
        return dispatch(fetchStudentForms())
            .catch(error => {
                dataDogAddWarning('fetchStudentForms: failed to load student forms', { error });
            })
            .then(() => {
                return dispatch(fetchMazeContentWithReload(true)).catch(error => {
                    dataDogAddWarning('fetchMazeContent: failed to reload maze content', { error });
                });
            });
    }
    const secure = selectors.isAssessmentSecure(state, MAZE.MEASURE);
    const { id } = form;
    const formFetchPromise = secure
        ? AssessmentFormsServiceClient.getSecureForm(
              id,
              selectors.getSchoolYear(state),
              MAZE_FETCH_RETRY,
          )
        : MazeServiceClient.getForm(null, null, id, MAZE_FETCH_RETRY);
    return dispatch(formFetchPromise).then(contentJson => {
        if (contentJson) {
            dispatch(setMazeContent(contentJson));
        }
    });
};

export const fetchMazeContent = () => fetchMazeContentWithReload(false);

export const completeMazeAssessment = assessment => (dispatch, getStore) => {
    const state = getStore();
    const language = selectors.getCurrentLanguage(state);
    dispatch(clearForm(selectors.getMazeFormId(state)));
    dispatch(clearScreenMode());
    dispatch(setPlaylistItemCompleted(assessment, language));
};

export const formatMazeResponses = responses => {
    // map of selected responses -> list of response objects
    const resultsList = Object.keys(responses).map(itemId => ({
        item_id: itemId,
        response_id: responses[itemId],
    }));
    const lastAttemptedResponseIndex = findLastIndex(resultsList, item => item.response_id != null);

    // add outcomes for not attempted responses
    return resultsList.map((item, index) => {
        if (item.response_id === null) {
            if (index < lastAttemptedResponseIndex) {
                return { ...item, outcome: RESULT_RESPONSE_OUTCOME.SKIPPED };
            }
            return { ...item, outcome: RESULT_RESPONSE_OUTCOME.NOT_ATTEMPTED };
        }
        return item;
    });
};

export const saveMazeResponse = (isDiscontinued, isRetry) => async (dispatch, getStore) => {
    const state = getStore();
    const formId = selectors.getMazeFormId(state);
    const selectedResponses = selectors.getMazeSelectedResponses(state);
    const resultResponse = formatMazeResponses(selectedResponses, isDiscontinued);

    if (isRetry) {
        await dispatch(getStudentResults(MAZE));

        const result = selectors.findResultFor(getStore(), { measure, formId });

        if (result) {
            return Promise.resolve();
        }
    }

    return dispatch(saveMeasureResponse(formId, resultResponse));
};

/**
 * Populates initial values for Maze selected responses based on questions ids
 * from content in the next format:
 *    {
 *        questionId1: null,
 *        questionId2: null,
 *        ...
 *        questionIdN: null,
 *    }
 */
export const initResponsesStructure = () => (dispatch, getStore) => {
    const state = getStore();
    const questionItems = selectors.getMazeQuestionItems(state);
    const responsesItems = questionItems.reduce(
        (result, item) => ({ ...result, [item.id]: null }),
        {},
    );
    dispatch(createResponsesStructure(responsesItems));
};

export const cacheMazeInstructions = () => dispatch =>
    Promise.all(
        ALL_MAZE_INSTRUCTIONS.map(instruction => dispatch(getMeasureInstruction(instruction))),
    );

export const setIsMazeUntimed = isMazeUntimed => ({
    type: SET_IS_MAZE_UNTIMED,
    isMazeUntimed,
});
